import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="support-request"
export default class extends Controller {
  static targets = ['modal', 'ulid'];

  connect() {
    this.recentErrors = [];
    this.boundOnError = this.onError.bind(this);
    this.boundOnUnhandledRejection = this.onUnhandledRejection.bind(this);
    this.boundOnTurboFetchError = this.onTurboFetchError.bind(this);

    window.addEventListener('error', this.boundOnError);
    window.addEventListener('unhandledrejection', this.boundOnUnhandledRejection);
    document.addEventListener('turbo:fetch-request-error', this.boundOnTurboFetchError);
  }

  disconnect() {
    window.removeEventListener('error', this.boundOnError);
    window.removeEventListener('unhandledrejection', this.boundOnUnhandledRejection);
    document.removeEventListener('turbo:fetch-request-error', this.boundOnTurboFetchError);
  }

  async generateCode() {
    try {
      if (this.hasUlidTarget) {
        this.ulidTarget.textContent = 'Wird generiert...';
      }

      const response = await fetch('/staff/support_requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(this.browserPayload())
      });

      if (response.ok) {
        const data = await response.json();
        if (this.hasUlidTarget) {
          this.ulidTarget.textContent = data.ulid;
        }
      } else {
        throw new Error('Failed to generate support code');
      }
    } catch (error) {
      console.error('Error generating support code:', error);
      if (this.hasUlidTarget) {
        this.ulidTarget.textContent = 'Fehler beim Generieren';
      }
    }
  }

  browserPayload() {
    return {
      page_url: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      browser_language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      recent_errors: this.recentErrors,
      ...this.editContext()
    };
  }

  // Turbo Frame edits often keep the browser URL on index — capture the open edit instead.
  editContext() {
    const editRoot = this.findActiveEditRoot();
    if (!editRoot) return {};

    const frame = editRoot.closest('turbo-frame');
    const form = editRoot.querySelector('form[action]');
    const frameSrc = this.absoluteUrl(frame?.getAttribute('src') || frame?.src);
    const formAction = this.absoluteUrl(form?.getAttribute('action'));
    const editUrl = frameSrc || formAction;

    return {
      edit_url: editUrl || null,
      turbo_frame_id: frame?.id || null,
      turbo_frame_src: frameSrc || null
    };
  }

  findActiveEditRoot() {
    const candidates = Array.from(
      document.querySelectorAll('article.staff-edit-box, section.staff-edit-box, .staff-edit-box')
    );

    return (
      candidates.find((element) => this.isVisible(element) && element.querySelector('form')) ||
      candidates.find((element) => this.isVisible(element)) ||
      null
    );
  }

  isVisible(element) {
    return !!(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));
  }

  absoluteUrl(url) {
    if (!url) return null;
    try {
      return new URL(url, window.location.origin).href;
    } catch (_error) {
      return url;
    }
  }

  onError(event) {
    this.pushError({
      type: 'error',
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno
    });
  }

  onUnhandledRejection(event) {
    const reason = event.reason;
    this.pushError({
      type: 'unhandledrejection',
      message: reason?.message || String(reason)
    });
  }

  onTurboFetchError(event) {
    const detail = event.detail || {};
    this.pushError({
      type: 'turbo:fetch-request-error',
      message: detail.error?.message || detail.message || 'Turbo fetch request error',
      source: detail.url || detail.fetchOptions?.url
    });
  }

  pushError(error) {
    this.recentErrors = [
      ...this.recentErrors,
      {
        ...error,
        at: new Date().toISOString()
      }
    ].slice(-5);
  }
}
